/**
 * Recovers tabs that outlive a deploy. A tab keeps the chunk names of the
 * build it loaded; after a deploy those files are gone, so the next lazy
 * route import fails and the page stays blank until a hard refresh. Reloading
 * fetches the new index.html and its chunk names.
 */

const RELOADED_AT_KEY = "stale-chunk-reloaded-at";
/** One reload per window, so a broken build cannot loop the tab. */
const RELOAD_COOLDOWN_MS = 10_000;

// Chrome, Safari and Firefox word a failed dynamic import differently. The
// MIME message is what browsers say when the server answers with index.html.
const STALE_CHUNK_MESSAGES = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "is not a valid JavaScript MIME type",
  "Unable to preload CSS",
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? String(error.message)
      : String(error);
  return STALE_CHUNK_MESSAGES.some((fragment) => message.includes(fragment));
}

type ReloadDeps = {
  readonly storage: Pick<Storage, "getItem" | "setItem">;
  readonly now: number;
  readonly reload: () => void;
};

/** Reloads the page unless it already did so moments ago. Returns whether it reloaded. */
export function reloadForStaleChunk(deps?: ReloadDeps): boolean {
  const { storage, now, reload } = deps ?? {
    storage: window.sessionStorage,
    now: Date.now(),
    reload: () => window.location.reload(),
  };
  const last = Number(storage.getItem(RELOADED_AT_KEY) ?? 0);
  if (now - last < RELOAD_COOLDOWN_MS) return false;
  storage.setItem(RELOADED_AT_KEY, String(now));
  reload();
  return true;
}

/**
 * Reloads on Vite's `vite:preloadError`, which fires when a lazy import or its
 * preloaded CSS fails. If the cooldown blocks the reload, the error reaches the
 * route's error component as usual.
 */
export function installStaleChunkReload(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk()) event.preventDefault();
  });
}
